import ActionDialog from "@dashboard/components/ActionDialog";
import { useConditionalFilterContext } from "@dashboard/components/ConditionalFilter";
import { createCustomerQueryVariables } from "@dashboard/components/ConditionalFilter/queryVariables";
import DeleteFilterTabDialog from "@dashboard/components/DeleteFilterTabDialog";
import SaveFilterTabDialog from "@dashboard/components/SaveFilterTabDialog";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { useFlag } from "@dashboard/featureFlags";
import { useBulkRemoveCustomersMutation, useVendorListQuery } from "@dashboard/graphql";
import { useFilterPresets } from "@dashboard/hooks/useFilterPresets";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { useRowSelection } from "@dashboard/hooks/useRowSelection";
import { commonMessages, sectionNames } from "@dashboard/intl";
import { ListViews } from "@dashboard/types";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@dashboard/utils/handlers/filterHandlers";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import isEqual from "lodash/isEqual";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import VendorListPage from "../../components/VendorListPage";
import { vendorListUrl, VendorListUrlDialog, VendorListUrlQueryParams } from "../../urls";
import { getFilterOpts, getFilterQueryParam, getFilterVariables, storageUtils } from "./filters";
import { getSortQueryVariables } from "./sort";
import VendorDetailsDialog from "@dashboard/vendors/components/VendorDetailsDialog/VendorDetailsDialog";

interface VendorListProps {
  params: VendorListUrlQueryParams;
}

export const VendorList = ({ params }: VendorListProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.CUSTOMER_LIST);
  const { enabled: isVendorsFiltersEnabled } = useFlag("new_filters");
  const { valueProvider } = useConditionalFilterContext();
  const filter = createCustomerQueryVariables(valueProvider.value);

  usePaginationReset(vendorListUrl, params, settings.rowNumber);

  const {
    clearRowSelection,
    selectedRowIds,
    setClearDatagridRowSelectionCallback,
    setSelectedRowIds,
  } = useRowSelection(params);
  const {
    selectedPreset,
    presets,
    hasPresetsChanged,
    onPresetChange,
    onPresetDelete,
    onPresetSave,
    onPresetUpdate,
    setPresetIdToDelete,
    getPresetNameToDelete,
  } = useFilterPresets({
    params,
    reset: clearRowSelection,
    getUrl: vendorListUrl,
    storageUtils,
  });
  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params),
      search: params.query,
      includeVendors: true,
      PERMISSION_MANAGE_ORDERS: true,
    }),
    [params, settings.rowNumber],
  );
  const newQueryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        ...filter,
        search: params.query,
      },
      sort: getSortQueryVariables(params),
      search: params.query,
      includeVendors: true,
      PERMISSION_MANAGE_ORDERS: true,
    }),
    [params, settings.rowNumber, valueProvider.value, filter],
  );

  // Use the generated useVendorListQuery hook
  const { data, loading, refetch } = useVendorListQuery({
    displayLoader: true,
    variables: isVendorsFiltersEnabled ? newQueryVariables : queryVariables,
  });

  // Get vendors directly from the customers query result with includeVendors: true
  const vendors = useMemo(() => {
    return mapEdgesToItems(data?.customers) || [];
  }, [data?.customers]);

  const [changeFilters, resetFilters, handleSearchChange] = createFilterHandlers({
    cleanupFn: clearRowSelection,
    createUrl: vendorListUrl,
    getFilterQueryParam,
    navigate,
    params,
    keepActiveTab: true,
  });
  const [openModal, closeModal] = createDialogActionHandlers<
    VendorListUrlDialog,
    VendorListUrlQueryParams
  >(navigate, vendorListUrl, params);
  const paginationValues = usePaginator({
    pageInfo: data?.customers?.pageInfo,
    paginationState,
    queryString: params,
  });
  const [bulkRemoveVendors, bulkRemoveVendorsOpts] = useBulkRemoveCustomersMutation({
    onCompleted: data => {
      if (data.customerBulkDelete?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges),
        });
        refetch();
        clearRowSelection();
        closeModal();
      }
    },
  });
  const handleSort = createSortHandler(navigate, vendorListUrl, params);
  const handleSetSelectedVendorIds = useCallback(
    (rows: number[], clearSelection: () => void) => {
      if (!vendors) {
        return;
      }

      const rowsIds = rows.map(row => vendors[row]?.id).filter(id => id !== undefined);
      const haveSaveValues = isEqual(rowsIds, selectedRowIds);

      if (!haveSaveValues) {
        setSelectedRowIds(rowsIds);
      }

      setClearDatagridRowSelectionCallback(clearSelection);
    },
    [vendors, selectedRowIds, setClearDatagridRowSelectionCallback, setSelectedRowIds],
  );

  // NEW: vendor details dialog state
  const [isVendorDetailsOpen, setVendorDetailsOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  const handleViewVendor = useCallback((id: string) => {
    setSelectedVendorId(id);
    setVendorDetailsOpen(true);
  }, []);

  const handleVendorDetailsClose = useCallback(() => {
    setVendorDetailsOpen(false);
    setSelectedVendorId(null);
  }, []);

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <WindowTitle title={intl.formatMessage(sectionNames.vendors)} />
      <VendorListPage
        selectedFilterPreset={selectedPreset}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onFilterPresetsAll={resetFilters}
        onFilterPresetChange={onPresetChange}
        onFilterPresetDelete={(id: number) => {
          setPresetIdToDelete(id);
          openModal("delete-search");
        }}
        onFilterPresetPresetSave={() => openModal("save-search")}
        onFilterPresetUpdate={onPresetUpdate}
        filterPresets={presets.map(preset => preset.name)}
        vendors={vendors}
        settings={settings}
        disabled={!data}
        loading={loading}
        onUpdateListSettings={updateListSettings}
        onSort={handleSort}
        selectedVendorIds={selectedRowIds}
        onSelectVendorIds={handleSetSelectedVendorIds}
        sort={getSortParams(params)}
        hasPresetsChanged={hasPresetsChanged}
        onVendorsDelete={() => openModal("remove", { ids: selectedRowIds })}
        onViewVendor={handleViewVendor}
      />
      <ActionDialog
        open={params.action === "remove" && selectedRowIds?.length > 0}
        onClose={closeModal}
        confirmButtonState={bulkRemoveVendorsOpts.status}
        onConfirm={() =>
          bulkRemoveVendors({
            variables: {
              ids: selectedRowIds,
            },
          })
        }
        variant="delete"
        title={intl.formatMessage({
          id: "a8uCmh",
          defaultMessage: "Delete Vendors",
          description: "dialog header",
        })}
      >
        <FormattedMessage
          id="nozxjf"
          defaultMessage="{counter,plural,one{Are you sure you want to delete this vendor?} other{Are you sure you want to delete {displayQuantity} vendors?}}"
          values={{
            counter: selectedRowIds?.length,
            displayQuantity: <strong>{selectedRowIds?.length}</strong>,
          }}
        />
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetDelete}
        tabName={getPresetNameToDelete()}
      />
      {/* NEW: vendor details modal */}
      <VendorDetailsDialog
        open={isVendorDetailsOpen}
        vendorId={selectedVendorId}
        onClose={handleVendorDetailsClose}
      />
    </PaginatorContext.Provider>
  );
};
export default VendorList;
